import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ErrorDialog from './components/ErrorDialog';
import FloatingError from './components/FloatingError';
import EntraMigrationPrompt from './components/EntraMigrationPrompt';
import AppRoutes from './Routes';
import './App.css';
import { useAnalytics } from './hooks/useAnalytics';
import { UserContext } from './context/UserContext';
import { useGlobalTrackedClicks } from './apps/analytics/useGlobalTrackedClicks';
import { useAppViewTracking } from './analytics/useAppViewTracking';
import { useEntraBackGuard } from './auth/useEntraBackGuard';

function App() {
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorObject, setErrorObject] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(window.innerWidth >= 1024);
  const location = useLocation();

  const { i18n } = useTranslation();
  const { user, hospitals, isInitialized, authToken } = useContext(UserContext);

  const hospitalName = user?.roles?.[0]?.hospital
    ? hospitals.find((h) => h._id?.toString() === user.roles[0].hospital?.toString())?.nameEnglish
    : undefined;

  // For anonymous users (no token) fire immediately; for logged-in users wait until data is loaded.
  const analyticsReady = isInitialized || !authToken;

  useAnalytics(hospitalName, analyticsReady);

  useAppViewTracking();
  useEntraBackGuard();

  useGlobalTrackedClicks({
    getBaseParams: () => ({ hospital: hospitalName }),
    shouldTrack: (el) => !el.hasAttribute('data-track-skip'),
  });

  useEffect(() => {
    // Update language when URL changes
    const params = new URLSearchParams(location.search);
    const lang = params.get('lang');
    if (lang && ['en', 'he', 'ar'].includes(lang)) {
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage('he'); // Default to Hebrew
    }
  }, [location.search, i18n]);

  useEffect(() => {
    // disable scroll restoration
    document.getElementById('main-content')?.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    window.showErrorDialog = (error) => {
      setErrorObject(error);
      setErrorDialogOpen(true);
    };

    return () => {
      // Clean up the global error handler
      delete window.showErrorDialog;
    };
  }, []);

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorObject(null);
  };
  
  const isFullPage = ['/login', '/register', '/signin', '/signed-out', '/auth/callback'].includes(location.pathname)
    || location.pathname.startsWith('/onboarding');   

  return (
    <div className="App">
      <Header onMenuOpen={setSidebarExpanded} />
      <div className="content-wrapper">
        <main id="main-content" className={isFullPage ? 'full-width' : ''}>
          <AppRoutes />
        </main>
        {!isFullPage && <Sidebar isExpanded={sidebarExpanded} onToggle={setSidebarExpanded} />}
      </div>
      <ErrorDialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        error={errorObject}
      />
      <FloatingError />
      <EntraMigrationPrompt fullPage={isFullPage} />
    </div>
  );
}

export default App;